import { css } from "lit";
import { defaultTokens } from "./tokens";

export const sharedStyles = css`
  ${defaultTokens}

  :host {
    font-family: var(--cg-font);
    --sl-font-sans: var(--cg-font);
    --sl-font-mono: var(--cg-font);
    color: var(--cg-text);
    padding-bottom: env(safe-area-inset-bottom);
    font-synthesis: none;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  *,
  *::before,
  *::after {
    font-family: var(--cg-font);
  }

  button,
  input,
  textarea,
  select {
    font: inherit;
  }

  :host([default-open="true"]) {
    display: block;
    width: 100%;
    height: 100%;
    margin: 0 auto;
    padding-bottom: 0;
  }

  :host,
  .panel {
    transition:
      background-color 200ms ease,
      color 200ms ease;
  }

  /* ===========================
   Loader
=========================== */

  .loader-wrap {
    flex: 1;
    display: grid;
    place-items: center;
    padding: 24px;
    color: var(--cg-primary);
  }

  /* ===========================
     Launcher
  ============================ */

  .launcher {
    position: fixed;
    width: 60px;
    height: 60px;
    border-radius: 999px;
    border: 0;
    cursor: pointer;
    box-shadow: var(--cg-shadow);
    background: black;
    color: var(--cg-on-primary);
    display: flex;
    place-items: center;
    justify-content: center;
    z-index: 999999;
    transition:
      transform 160ms ease,
      box-shadow 160ms ease,
      background-color 200ms ease;
    animation: launcherIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    animation-delay: 0.3s;
  }

  .launcher:hover {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 14px 46px rgba(0, 0, 0, 0.18);
  }

  .launcher:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.16);
  }

  @media (max-width: 480px) {
    .launcher-shifted {
      display: none;
    }
  }

  /* ===========================
     Launcher Open Position
  ============================ */

  .launcher-shifted {
    opacity: 0.92;
    transform: scale(0.96);
  }

  /* ===========================
     Panel
  ============================ */

  .panel {
    position: fixed;
    width: min(420px, calc(100vw - 24px));
    height: min(
      640px,
      calc(var(--cg-vh, 1vh) * 100 - 88px - 12px - env(safe-area-inset-bottom))
    );
    border-radius: var(--cg-radius);
    background: var(--cg-bg);
    box-shadow: var(--cg-shadow);
    border: none;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    z-index: 999998; /* under launcher */
    gap: 10px;
  }

  .panel.embedded {
    position: relative;
    width: 80%;
    height: 99%;
    max-width: none;
    margin: 0 auto;
    bottom: auto !important;
    right: auto !important;
    left: auto !important;
    box-shadow: none;
    border: 1px solid var(--cg-border);
    animation: none;
  }

  @media (max-width: 480px) {
    .panel {
      width: 100%;
      height: 100%;
      border-radius: 0;
      bottom: 0 !important;
      right: 0 !important;
      left: 0 !important;
      border: none;
    }
  }

  /* ===========================
     Header (overlay layer)
  ============================ */

  .header {
    position: relative;
    z-index: 30;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--cg-border);
    background: var(--cg-primary);
    color: var(--cg-on-primary);
  }

  .header-actions {
    display: flex;
    gap: 6px;
    align-items: center;
    margin-left: auto;
  }

  .header button {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: grid;
    place-items: center;
    color: inherit; /* ✅ Force inherit from header color (on-primary) */
    transition:
      background 120ms ease,
      transform 120ms ease;
  }

  .header button:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  .header button:active {
    transform: scale(0.94);
  }

  .header-new-icon::part(base) {
    font-family: var(--cg-font);
    --sl-font-sans: var(--cg-font);
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.2); /* Subtle border */
    background: transparent; /* ✅ Transparent to show header color */
    color: inherit; /* ✅ Inferit header color */
    display: flex;
    justify-content: center;
    align-items: center;
    transition:
      background 120ms ease,
      transform 120ms ease,
      box-shadow 120ms ease;
  }

  .header-new-icon::part(base):hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .header-new-icon::part(base):active {
    transform: translateY(0);
    box-shadow: none;
  }
  .header-close-icon::part(base) {
    width: 32px;
    height: 32px;
    border-radius: 999px;
    border: 1px solid var(--cg-border);
    background: var(--cg-surface);
    color: var(--cg-muted);
    transition:
      background 120ms ease,
      transform 120ms ease,
      box-shadow 120ms ease;
  }

  .header-close-icon::part(base):hover {
    background: rgba(0, 0, 0, 0.06);
    color: var(--cg-text);
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  }

  .header-close-icon::part(base):active {
    transform: translateY(0);
    box-shadow: none;
  }

  /* Overflow menu must float above header */
  cg-overflow-menu {
    position: relative;
    z-index: 40;
  }

  /* ===========================
     Body
  ============================ */

  .body {
    position: relative;
    z-index: 10;
    flex: 1;
    padding: 0; /* ✅ important */
    overflow: hidden; /* ✅ important */
    display: flex;
    flex-direction: column;
    min-height: 0; /* ✅ fixes flex scroll issues */
  }

  /* ===========================
     Footer
  ============================ */

  .footer {
    position: relative;
    z-index: 20;
    padding: 10px 12px;
    border-top: 1px solid var(--cg-border);
    background: var(--cg-surface);
  }

  .muted {
    color: var(--cg-muted);
    font-size: 12px;
  }

  /* ===========================
     Screen System
  ============================ */

  .screen-wrap {
    position: relative;
    z-index: 0; /* keep behind header overlays */
    flex: 1;
    overflow: hidden;
  }

  .screen-track {
    position: absolute;
    inset: 0;
    z-index: 0; /* critical: prevents hit-test blocking */
    display: flex;
    width: 200%;
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
  }

  .screen {
    width: 50%;
    height: 100%;
    overflow: hidden;
    margin-bottom: 16px;
  }

  /* Track transforms */

  .track-chat {
    transform: translateX(0%);
  }

  .track-recent {
    transform: translateX(-50%);
  }

  @media (pointer: coarse) {
    button {
      min-height: 40px;
    }
  }

  /* ===========================
     Footer Layout (Polish)
  ============================ */

  .footer {
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .suggestions {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 2px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .suggestions::-webkit-scrollbar {
    display: none;
  }

  .chip {
    flex: 0 0 auto;
    white-space: nowrap;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid var(--cg-border);
    background: var(--cg-bg);
    font-size: 13px;
    cursor: pointer;
    line-height: 1;
  }
  .chip:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .composer-row {
    position: relative; /* ✅ anchor emoji picker */
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }

  .footer-meta {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ===========================
     Emoji Picker Containment
  ============================ */

  .emoji-pop {
    position: absolute;
    left: 0;
    bottom: calc(100% + 8px);
    z-index: 60;
    border: 1px solid var(--cg-border);
    border-radius: 12px;
    overflow: hidden;
    background: var(--cg-bg);
    box-shadow: var(--cg-shadow);
    max-height: 280px;
  }

  @media (max-width: 480px) {
    .emoji-pop {
      left: 0;
      right: 0;
      max-width: 100%;
    }
  }

  .panel {
    animation: panelIn 160ms ease-out;
  }

  @keyframes panelIn {
    from {
      transform: translateY(6px) scale(0.98);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  .branding {
    font-size: 11px;
    color: var(--cg-muted);
  }

  .branding strong {
    font-weight: 600;
    color: var(--cg-text);
  }

  .error-card {
    margin: 12px;
    padding: 14px 16px;
    border-radius: 14px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    animation: fadeIn 0.2s ease;
  }

  .error-init,
  .error-conversation,
  .error-stream {
    background: rgba(239, 68, 68, 0.06);
    border: 1px solid rgba(239, 68, 68, 0.25);
  }

  .error-offline {
    background: rgba(245, 158, 11, 0.08);
    border: 1px solid rgba(245, 158, 11, 0.3);
  }

  .error-icon {
    font-size: 22px;
    line-height: 1;
  }

  .error-title {
    font-weight: 700;
    font-size: 14px;
  }

  .error-description {
    font-size: 13px;
    opacity: 0.75;
    margin-top: 2px;
  }

  .error-action {
    margin-top: 10px;
    padding: 6px 12px;
    border-radius: 8px;
    border: 0;
    background: var(--cg-primary);
    color: var(--cg-on-primary);
    font-size: 12px;
    cursor: pointer;
  }

  .empty-state {
    height: 100%;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .empty-title {
    font-size: 16px;
    font-weight: 700;
  }

  .empty-subtitle {
    font-size: 13px;
    opacity: 0.7;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .header-loading,
  .header-error,
  .header-ready {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  .avatar.skeleton {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
    animation: shimmer 1.4s infinite;
  }

  .avatar.warning {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: rgba(255, 0, 0, 0.1);
  }

  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: 200px 0;
    }
  }

  /* ===========================
     Initial Message Popup
  ============================ */

  /* ===========================
     Initial Message Popup
  ============================ */

  .initial-message-popup {
    position: fixed;
    bottom: calc(
      88px + env(safe-area-inset-bottom)
    ); /* Launcher height + padding */
    z-index: 999999;
    max-width: min(calc(100vw - 32px), 400px);
    display: flex;
    flex-direction: column;
    align-items: flex-end; /* Align right by default */
    gap: 8px;
    pointer-events: none; /* Let clicks pass through container */
  }

  .initial-message-popup.left {
    align-items: flex-start;
  }

  .initial-message-bubble {
    pointer-events: auto;
    padding: 15px 15px;
    background: white;
    color: black;
    border-radius: 10px;
    border: none;
    box-shadow:
      rgba(150, 150, 150, 0.2) 0px 10px 30px 0px,
      rgba(150, 150, 150, 0.2) 0px 0px 0px 1px;
    cursor: pointer;
    font-size: 15px;
    line-height: 1.5;
    animation: popupIn 0.5s cubic-bezier(0.19, 1, 0.22, 1) both;
    animation-delay: 1.5s;
    transform-origin: bottom right;
    backdrop-filter: blur(8px);
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
    position: relative;
    font-weight: 500;
    margin-bottom: 8px;
    width: auto;
    max-width: 100%;
  }

  /* Stagger animation for multiple bubbles (base delay 1.5s) */
  .initial-message-bubble:nth-child(2) {
    animation-delay: 1.6s;
  }
  .initial-message-bubble:nth-child(3) {
    animation-delay: 1.7s;
  }
  .initial-message-bubble:nth-child(4) {
    animation-delay: 1.8s;
  }

  .initial-message-popup.left .initial-message-bubble {
    border-bottom-right-radius: 18px;
    border-bottom-left-radius: 4px;
    transform-origin: bottom left;
  }

  .initial-message-bubble:hover {
    transform: translateY(-2px);
    box-shadow:
      rgba(150, 150, 150, 0.3) 0px 15px 45px 0px,
      rgba(150, 150, 150, 0.1) 0px 0px 0px 1px;
  }

  .initial-message-close {
    pointer-events: auto;
    /* absolute positioning relative to the top-most bubble or container? 
       Actually, let's make it floating relative to the *last* bubble or the container top.
       Better design: Render it as a standalone floating button above the stack or attached to the top bubble.
    */
    position: absolute;
    top: -12px;
    right: -12px;
    width: 26px;
    height: 26px;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.94);
    color: black;
    border: 1px solid rgba(255, 255, 255, 0.94);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    padding: 0;
    transition: all 0.2s ease;
    opacity: 0;
    transform: scale(0.8);
    animation: fadeInScale 0.2s ease 1.6s forwards;
    z-index: 10;
  }

  .initial-message-popup.left .initial-message-close {
    right: auto;
    left: -12px;
  }

  @keyframes popupIn {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.9) skewX(-2deg);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1) skewX(0);
    }
  }

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.6);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes launcherIn {
    0% {
      transform: scale(0) rotate(-20deg) translateY(60px);
      opacity: 0;
    }
    40% {
      transform: scale(1.2) rotate(10deg) translateY(-20px);
      opacity: 1;
    }
    60% {
      transform: scale(0.9) rotate(-5deg) translateY(10px);
    }
    80% {
      transform: scale(1.05) rotate(2deg) translateY(-2px);
    }
    100% {
      transform: scale(1) rotate(0deg) translateY(0);
      opacity: 1;
    }
  }
`;
